import React from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { CountrySelector } from "./CountrySelector";
import { LiveJobsBar } from "./LiveJobsBar";
import { Sun, ChartBar, Buildings, Handshake, Envelope, PaperPlaneTilt, Factory, Storefront, ClockCounterClockwise, Receipt, Scales, SignOut } from "@phosphor-icons/react";
import { clearToken, isAuthed } from "../lib/auth";

const ADMIN_NAV = [
  { to: "/", label: "OVERVIEW", icon: ChartBar, end: true },
  { to: "/builders", label: "INSTALLERS", icon: Buildings },
  { to: "/manufacturers", label: "MANUFACTURERS", icon: Factory },
  { to: "/associations", label: "BUYERS", icon: Handshake },
  { to: "/matches", label: "MATCHES", icon: PaperPlaneTilt },
  { to: "/outreach", label: "OUTREACH", icon: Envelope },
  { to: "/history", label: "HISTORY", icon: ClockCounterClockwise },
  { to: "/invoices", label: "INVOICES", icon: Receipt },
  { to: "/products", label: "PRODUCTS", icon: Storefront },
];

const PUBLIC_NAV = [
  { to: "/products", label: "PRODUCTS", icon: Storefront },
  { to: "/terms",    label: "TERMS",    icon: Scales },
];

const PUBLIC_PATHS = new Set(["/", "/products", "/terms"]);

export const Layout = ({ children }) => {
  const loc = useLocation();
  const nav = useNavigate();
  const authed = isAuthed();
  const isPublic = PUBLIC_PATHS.has(loc.pathname) && !(loc.pathname === "/" && authed);
  const navItems = isPublic ? PUBLIC_NAV : ADMIN_NAV;

  const logout = () => {
    clearToken();
    nav("/products", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#0A0A0A]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-[#3E8B3A] shadow-sm" data-testid="app-header">
        <div className="px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Link to={isPublic ? "/products" : "/"} className="flex items-center gap-3 group">
            <div className="w-9 h-9 flex items-center justify-center transition-transform duration-200 group-hover:rotate-12">
              <Sun size={26} weight="bold" color="#0A0A0A" />
            </div>
            <div>
              <div className="text-sm tracking-[0.22em] leading-none text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                {isPublic ? "SOLARBRIDGE" : "HELIOS · LEADS"}
              </div>
              <div className="text-[10px] tracking-[0.18em] text-[#0A0A0A]/70 uppercase mt-1">
                {isPublic ? "EU Solar B2B Contact Lists" : "EU Solar Prospecting Engine"}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {!isPublic && <CountrySelector />}
            {!isPublic && authed && (
              <button onClick={logout} data-testid="admin-logout" title="Sign out"
                className="hidden sm:inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#0A0A0A] hover:bg-[#0A0A0A]/10 rounded-md px-3 py-1.5 transition-colors duration-150"
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                <SignOut size={11} weight="bold" /> SIGN OUT
              </button>
            )}
          </div>
        </div>
        <nav className="px-2 sm:px-6 flex overflow-x-auto bg-[#3E8B3A]" data-testid={isPublic ? "public-nav" : "admin-nav"}>
          {navItems.map((n) => {
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`px-3 sm:px-4 py-3 text-xs tracking-[0.2em] flex items-center gap-2 whitespace-nowrap transition-all duration-200 relative
                  ${active
                    ? "text-[#0A0A0A]"
                    : "text-[#0A0A0A]/70 hover:text-[#0A0A0A]"
                  }`}
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                <Icon size={13} weight="bold" />
                {n.label}
                {active && <span className="absolute bottom-0 left-3 right-3 h-[3px] bg-[#0A0A0A] rounded-t-full"></span>}
              </NavLink>
            );
          })}
        </nav>
      </header>
      {!isPublic && <LiveJobsBar />}
      <main className="pb-16 sm:pb-8" style={{ paddingBottom: "env(safe-area-inset-bottom, 32px)" }}>
        {children}
      </main>
      {isPublic && (
        <footer className="border-t border-[#0A0A0A]/10 mt-16 py-8 px-4 sm:px-8 text-xs text-[#525252] bg-white" data-testid="public-footer">
          <div className="max-w-7xl flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <Sun size={14} weight="bold" color="#0A0A0A" />
              </div>
              © 2026 SolarBridge · Lithuania
            </div>
            <div className="flex gap-5">
              <Link to="/terms" className="hover:text-[#3E8B3A] transition-colors">Terms of sale</Link>
              <a href="mailto:contact@solarbridge.eu" className="hover:text-[#3E8B3A] transition-colors">contact@solarbridge.eu</a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};
